import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  Input,
  Select,
  Text,
  VStack,
  Tooltip,
} from '@chakra-ui/react';

/** -------------------------------------------------
 * Helpers
 * ------------------------------------------------- */
const SLOT_OPTIONS = [
  '07:00–09:00',
  '09:00–11:00',
  '11:00–13:00',
  '13:00–15:00',
  '15:00–17:00',
  '17:00–19:00',
  '19:00–21:00',
];

const toEnDash = (s) =>
  s ? String(s).trim().replace(/\s*-\s*/g, '–').replace(/\s*–\s*/g, '–') : '';

const slotToString = (raw) => {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') return String(raw.label ?? raw.value ?? '');
  return String(raw);
};
const normaliseSlotInput = (raw) => toEnDash(slotToString(raw));

const todayIso = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const SURVEY_STAGES = [
  { value: 'awaiting_survey', label: 'Awaiting Survey' },
  { value: 'survey_booked', label: 'Survey Booked' },
  { value: 'survey_assigned', label: 'Survey Assigned' },
];

function changedPatch(initial, current) {
  const patch = {};
  ['postSaleStage', 'surveyDate', 'surveySlot', 'surveyorName'].forEach((k) => {
    if ((initial[k] || '') !== (current[k] || '')) patch[k] = current[k] || '';
  });

  // If time slot is cleared, also clear legacy surveyTime (safe no-op if not present server-side)
  if ((current.surveySlot || '') === '') {
    patch.surveyTime = '';
  }

  return patch;
}

/** -------------------------------------------------
 * Component
 * ------------------------------------------------- */
export default function SurveyManager({
  row,                 // { leadId, postSaleStage, surveyDate, surveySlot, surveyorName }
  canEdit = true,
  isSaving = false,
  onSave,              // (patch) => Promise<void> | void
}) {
  // Guard against missing row
  const initial = useMemo(
    () => ({
      leadId: row?.leadId || '',
      postSaleStage: row?.postSaleStage || 'awaiting_survey',
      surveyDate: row?.surveyDate || '',
      surveySlot: normaliseSlotInput(row?.surveySlot || ''),
      surveyorName: row?.surveyorName || '',
    }),
    [row]
  );

  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState({ date: false, slot: false, surveyor: false, stage: false });
  const [errors, setErrors] = useState({ date: '', slot: '', surveyor: '' });

  useEffect(() => {
    // refresh when row changes (e.g., switching deals)
    setForm({
      leadId: row?.leadId || '',
      postSaleStage: row?.postSaleStage || 'awaiting_survey',
      surveyDate: row?.surveyDate || '',
      surveySlot: normaliseSlotInput(row?.surveySlot || ''),
      surveyorName: row?.surveyorName || '',
    });
    setTouched({ date: false, slot: false, surveyor: false, stage: false });
    setErrors({ date: '', slot: '', surveyor: '' });
  }, [row]);

  /** -----------------------------
   * Auto-promotion (gentle)
   * ----------------------------- */
  useEffect(() => {
    if (form.postSaleStage === 'awaiting_survey' && form.surveyDate && form.surveySlot) {
      setForm((f) => ({ ...f, postSaleStage: 'survey_booked' }));
    }
  }, [form.surveyDate, form.surveySlot, form.postSaleStage]);

  useEffect(() => {
    if ((form.postSaleStage === 'awaiting_survey' || form.postSaleStage === 'survey_booked') && form.surveyorName.trim()) {
      setForm((f) => ({ ...f, postSaleStage: 'survey_assigned' }));
    }
  }, [form.surveyorName, form.postSaleStage]);

  /** -----------------------------
   * Validation
   * ----------------------------- */
  const validate = useCallback((state) => {
    const next = { date: '', slot: '', surveyor: '' };
    const requiresDateSlot = state.postSaleStage === 'survey_booked' || state.postSaleStage === 'survey_assigned';
    const requiresSurveyor = state.postSaleStage === 'survey_assigned';

    if (requiresDateSlot) {
      if (!state.surveyDate) next.date = 'Date is required.';
      else if (state.surveyDate < todayIso()) next.date = 'Date cannot be in the past.';

      if (!state.surveySlot) next.slot = 'Time slot is required.';
    }

    if (requiresSurveyor) {
      if (!state.surveyorName.trim()) next.surveyor = 'Surveyor is required.';
    }
    return next;
  }, []);

  useEffect(() => {
    setErrors(validate(form));
  }, [form, validate]);

  /** -----------------------------
   * Handlers
   * ----------------------------- */
  const onChange = (key) => (e) => {
    const value = key === 'surveySlot' ? normaliseSlotInput(e?.target?.value ?? e) : e?.target?.value ?? '';
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onBlur = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const disabled = !canEdit || isSaving;

  const handleSave = async () => {
    if (!onSave || !form.leadId) return;
    const errs = validate(form);
    setErrors(errs);
    setTouched({ date: true, slot: true, surveyor: true, stage: true });
    const hasError = Object.values(errs).some(Boolean);
    if (hasError) return;

    const patch = changedPatch(initial, form);
    if (!Object.keys(patch).length) {
      // nothing changed; skip no-op writes
      return;
    }
    await onSave(patch);
  };

  const handleReset = () => {
    // Reset form to Awaiting Survey with cleared date/slot/surveyor; user must press Save to commit
    setForm((f) => ({
      leadId: f.leadId,
      postSaleStage: 'awaiting_survey',
      surveyDate: '',
      surveySlot: '',
      surveyorName: '',
    }));
    setTouched({ date: false, slot: false, surveyor: false, stage: true });
    setErrors({ date: '', slot: '', surveyor: '' });
  };

  const isAlreadyAwaitingAndClear =
    form.postSaleStage === 'awaiting_survey' &&
    !form.surveyDate &&
    !form.surveySlot &&
    !form.surveyorName;

  /** -----------------------------
   * UI
   * ----------------------------- */
  return (
    <Box>
      <Text fontWeight="semibold" mb={3}>Survey Management Area</Text>

      <VStack align="stretch" spacing={3}>
        <FormControl isDisabled={disabled}>
          <FormLabel>Stage</FormLabel>
          <Select
            value={form.postSaleStage}
            onChange={(e) => setForm((f) => ({ ...f, postSaleStage: e.target.value }))}
            onBlur={onBlur('stage')}
          >
            {SURVEY_STAGES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Select>
        </FormControl>

        <HStack spacing={3} align="start">
          <FormControl isInvalid={!!errors.date && touched.date} isDisabled={disabled}>
            <FormLabel>Date</FormLabel>
            <Input
              type="date"
              value={form.surveyDate}
              min={todayIso()}
              onChange={onChange('surveyDate')}
              onBlur={onBlur('date')}
            />
            <FormErrorMessage>{errors.date}</FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.slot && touched.slot} isDisabled={disabled}>
            <FormLabel>Time slot</FormLabel>
            <Select
              placeholder="Select a slot"
              value={form.surveySlot}
              onChange={onChange('surveySlot')}
              onBlur={onBlur('slot')}
            >
              {SLOT_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
              {/* Keep existing value visible even if custom */}
              {form.surveySlot && !SLOT_OPTIONS.includes(form.surveySlot) && (
                <option value={form.surveySlot}>{form.surveySlot} (custom)</option>
              )}
            </Select>
            <FormErrorMessage>{errors.slot}</FormErrorMessage>
          </FormControl>
        </HStack>

        <FormControl isInvalid={!!errors.surveyor && touched.surveyor} isDisabled={disabled}>
          <FormLabel>Surveyor</FormLabel>
          <Input
            placeholder="Type full name"
            value={form.surveyorName}
            onChange={(e) => setForm((f) => ({ ...f, surveyorName: e.target.value }))}
            onBlur={onBlur('surveyor')}
          />
          <FormErrorMessage>{errors.surveyor}</FormErrorMessage>
        </FormControl>

        <HStack pt={2} justify="space-between">
          <Tooltip label="Resets to 'Awaiting Survey'. Then Save it" hasArrow>
            <Button
              variant="outline"
              onClick={handleReset}
              isDisabled={disabled || isAlreadyAwaitingAndClear}
            >
              Reset survey
            </Button>
          </Tooltip>

          <Tooltip label="Click to Save" hasArrow>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={isSaving}
              isDisabled={disabled}
            >
              Save
            </Button>
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );
}
